import { format, isToday, isTomorrow } from "date-fns";
import { enUS, es } from "date-fns/locale";

import { SupportedLanguage, i18n, t } from "i18n/i18n";

/** Time-input parse/display pattern. Kept stable so typed values still parse. */
export const TIME_FORMAT = "h:mm a";

const DATE_FNS_LOCALES = { en: enUS, es };

export const getDateFnsLocale = () =>
  DATE_FNS_LOCALES[(i18n.language as SupportedLanguage) || "en"] ?? enUS;

type DateInput = Date | string | number;

const toDate = (date: DateInput): Date | null => {
  if (typeof date === "string" && date.trim() === "") return null;

  const value = date instanceof Date ? date : new Date(date);

  return Number.isNaN(value.getTime()) ? null : value;
};

export type DateFormatKey =
  | "common.formats.long_month_day_year"
  | "common.formats.month_day_time"
  | "common.formats.month_day_year"
  | "common.formats.month_day_year_time_12"
  | "common.formats.month_day_year_time"
  | "common.formats.numeric"
  | "common.formats.time";

/** Format with the date-fns pattern stored for the active locale. */
export const formatLocalePattern = (date: DateInput, patternKey: DateFormatKey): string => {
  const value = toDate(date);

  if (!value) {
    return typeof date === "string" ? date : "";
  }

  return format(value, t(patternKey), { locale: getDateFnsLocale() });
};

export const monthDayFormat = (date: Date) => ({
  val: date,
  formatParams: {
    val: { day: "numeric", month: "short" },
  },
});

const monthDayAtTimeFormatters = new Map<string, Intl.DateTimeFormat>();

const getMonthDayAtTimeFormatter = (language: string) => {
  const cached = monthDayAtTimeFormatters.get(language);
  if (cached) return cached;
  // Cached per language; Intl construction is expensive.
  // react-doctor-disable-next-line react-doctor/js-hoist-intl
  const formatter = new Intl.DateTimeFormat(language, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
  monthDayAtTimeFormatters.set(language, formatter);
  return formatter;
};

export const monthDayAtTime = (dateStr: string) => {
  if (!dateStr) return {};

  const date = new Date(dateStr);
  const dateParts = getMonthDayAtTimeFormatter(i18n.language).formatToParts(date);

  const month = dateParts.find((part) => part.type === "month")?.value;
  const day = dateParts.find((part) => part.type === "day")?.value;
  const hour = dateParts.find((part) => part.type === "hour")?.value;
  const minute = dateParts.find((part) => part.type === "minute")?.value;
  const dayPeriod = dateParts.find((part) => part.type === "dayPeriod")?.value;

  const time = `${hour}:${minute} ${dayPeriod}`;

  return { month, day, time };
};

export const relativeDayAndTimeFormat = (date: Date) => {
  if (isToday(date)) {
    return `${t("common.today")}, ${formatLocalePattern(date, "common.formats.time")}`;
  }

  if (isTomorrow(date)) {
    return `${t("common.tomorrow")}, ${formatLocalePattern(date, "common.formats.time")}`;
  }

  return formatLocalePattern(date, "common.formats.month_day_time");
};

export const weekdayMonthDayFormat = (date: Date) => ({
  val: date,
  formatParams: {
    val: { weekday: "short", month: "short", day: "numeric" },
  },
});

export const weekdayMonthDayAtTimeFormat = (date: Date) => ({
  val: date,
  formatParams: {
    val: { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "numeric" },
  },
});
